import { Capacitor } from '@capacitor/core'
import { env } from './env'
import { getAuthItem, setAuthItem, removeAuthItem } from './authStorage'
import { getSelectedWorkspaceId } from './api/workspace-context'

const isNative = Capacitor.isNativePlatform()
const REFRESH_TOKEN_KEY = 'xavior_refresh_token'
type RequestOptions = Omit<RequestInit, 'headers'> & {
  headers?: HeadersInit
  searchParams?: Record<string, string>
  authToken?: string
  guestId?: string
  skipRetry?: boolean // Internal flag to prevent infinite retry loops
}

export class ApiError extends Error {
  readonly status: number
  readonly payload?: unknown

  constructor(message: string, status: number, payload?: unknown) {
    super(message)
    this.status = status
    this.payload = payload
  }
}

export class ContainerWarmingUpError extends Error {
  readonly retryAfter: number

  constructor(message: string, retryAfter: number = 3) {
    super(message)
    this.name = 'ContainerWarmingUpError'
    this.retryAfter = retryAfter
  }
}

/**
 * Creates a full URL by combining the appropriate base URL with a path.
 * Auth endpoints (/auth/*) go to the supervisor, others go to the API gateway.
 */
function createUrl(path: string, searchParams?: Record<string, string>): string {
  // Auth endpoints go to the supervisor (authBaseUrl), others to the API gateway
  const isAuthEndpoint = path.startsWith('/auth') || path.startsWith('auth')
  const base = isAuthEndpoint ? env.authBaseUrl : env.apiBaseUrl

  // Normalize: ensure base has no trailing slash, path has leading slash
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  const url = new URL(`${normalizedBase}${normalizedPath}`)

  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      url.searchParams.set(key, value)
    }
  }

  return url.toString()
}

/**
 * Callback registered by SessionProvider so that when apiClient refreshes a
 * token internally (on 401 retry), React state is updated too. Without this,
 * components keep sending the stale expired token on every render even though
 * the new token is already sitting in localStorage.
 */
let onTokenRefreshed: ((token: string) => void) | null = null
export function setTokenRefreshCallback(cb: (token: string) => void): void {
  onTokenRefreshed = cb
}

/**
 * Mutex to prevent multiple refresh requests from running concurrently.
 * This is critical when refresh tokens rotate - if two requests try to refresh
 * at the same time, one will succeed and rotate the token, causing the other to fail.
 */
let refreshInFlight: Promise<string | null> | null = null

/**
 * Try to refresh the access token using the refresh token cookie.
 * Returns the new access token or null if refresh failed.
 *
 * IMPORTANT: This function is mutex-protected - if multiple calls happen
 * simultaneously (e.g., from StrictMode double-mounting), only ONE actual
 * network request will be made, and all callers will get the same result.
 */
async function tryRefreshToken(): Promise<string | null> {
  // If a refresh is already in flight, return that promise instead of starting a new one
  if (refreshInFlight) {
    console.log('[apiClient] Refresh already in flight, waiting for existing request...')
    return refreshInFlight
  }

  refreshInFlight = (async () => {
    try {
      console.log('[apiClient] Attempting to refresh access token...')
      const storedToken = typeof window !== 'undefined' ? getAuthItem('xavior_auth_token') : null

      // Build headers — native apps send refresh token via header instead of cookie
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`
      }
      if (isNative) {
        headers['X-Client-Platform'] = 'capacitor'
        const storedRefresh = getAuthItem(REFRESH_TOKEN_KEY)
        if (storedRefresh) {
          headers['X-Refresh-Token'] = storedRefresh
        }
      }

      const response = await fetch(createUrl('/auth/refresh'), {
        method: 'POST',
        credentials: 'include', // Still send cookie as fallback
        headers,
      })

      if (!response.ok) {
        console.log('[apiClient] Refresh failed, status:', response.status)
        return null
      }

      const data = await response.json()
      console.log('[apiClient] Refresh successful, got new access token')

      // Store new access token in auth storage (localStorage on native, sessionStorage on web)
      if (typeof window !== 'undefined') {
        setAuthItem('xavior_auth_token', data.accessToken)
        if (data.email) {
          setAuthItem('userEmail', data.email)
        }
        // Store refresh token for native apps (returned in body instead of cookie)
        if (isNative && data.refreshToken) {
          setAuthItem(REFRESH_TOKEN_KEY, data.refreshToken)
          console.log('[apiClient] Stored refresh token in localStorage for native persistence')
        }
      }

      // Propagate to SessionProvider React state so components get the new token
      // without needing a page reload.
      onTokenRefreshed?.(data.accessToken)

      return data.accessToken as string
    } catch (err) {
      console.error('[apiClient] Refresh token request failed:', err)
      return null
    } finally {
      // Clear the in-flight promise so future calls can make a new request
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

/**
 * Public API for refreshing the access token.
 * This is called by SessionProvider on initial load to restore sessions.
 */
export async function refreshAccessToken(): Promise<string | null> {
  return tryRefreshToken()
}

export async function apiFetch<T = unknown>(
  path: string,
  { searchParams, authToken, guestId, headers, skipRetry, ...init }: RequestOptions = {},
): Promise<T> {
  const url = createUrl(path, searchParams)

  const requestHeaders = new Headers(headers)

  requestHeaders.set('Accept', 'application/json')

  // Set Content-Type if body is present
  if (init.body && typeof init.body === 'string') {
    requestHeaders.set('Content-Type', 'application/json')
  }

  if (authToken) {
    requestHeaders.set('Authorization', `Bearer ${authToken}`)
    if (typeof window !== 'undefined') {
      const storedEmail = getAuthItem('userEmail')
      if (storedEmail) {
        requestHeaders.set('X-User-Email', storedEmail)
      }
    }
    const workspaceId = getSelectedWorkspaceId()
    if (workspaceId) {
      requestHeaders.set('X-Workspace-Id', workspaceId)
    }
  } else if (guestId) {
    // Send guest ID when not authenticated
    requestHeaders.set('X-Guest-Id', guestId)
  }

  const response = await fetch(url, {
    ...init,
    headers: requestHeaders,
    credentials: 'include', // CRITICAL: Send session cookies with every request
  })

  const contentType = response.headers.get('Content-Type') ?? ''
  const isJson = contentType.includes('application/json')

  let data: unknown = null

  if (isJson) {
    try {
      data = await response.json()
    } catch (error) {
      console.warn('apiFetch: failed to parse JSON', error)
    }
  } else {
    data = await response.text()
  }

  if (!response.ok) {
    // Check for container warming up (503 with special header)
    if (response.status === 503 && response.headers.get('X-Container-Status') === 'warming-up') {
      const retryAfter = parseInt(response.headers.get('Retry-After') || '3', 10)
      const message = typeof data === 'object' && data !== null && 'message' in data
        ? (data as { message: string }).message
        : 'Your secure container is starting up...'

      throw new ContainerWarmingUpError(message, retryAfter)
    }

    // Handle 401 Unauthorized - try to refresh token and retry once
    if (response.status === 401 && authToken && !skipRetry && path !== '/auth/refresh') {
      console.log('[apiClient] Got 401, attempting token refresh and retry...')
      const newToken = await tryRefreshToken()

      if (newToken) {
        console.log('[apiClient] Retrying request with new token')
        // Retry the request with the new token (skipRetry prevents infinite loop)
        return apiFetch<T>(path, {
          ...init,
          searchParams,
          authToken: newToken,
          guestId,
          headers,
          skipRetry: true,
        })
      } else {
        console.log('[apiClient] Token refresh failed, clearing session')
        // Clear session and let user re-login
        if (typeof window !== 'undefined') {
          removeAuthItem('xavior_auth_token')
          removeAuthItem('userEmail')
        }
      }
    }

    throw new ApiError(
      `Request to ${path} failed with status ${response.status}`,
      response.status,
      data,
    )
  }

  return data as T
}

